import { create } from 'zustand';
import { canStartWave, type World } from '@playnets/sim';
import { LEVELS } from '@playnets/data';

/**
 * React never touches the simulation (§12.2). This store is the one-way valve:
 * the sim pushes a flat snapshot into it at 10Hz, React subscribes. Nothing in
 * here is a game entity, and nothing here is read back by the sim.
 *
 * Re-rendering React sixty times a second is how these games die.
 */
export type Screen = 'select' | 'map' | 'armoury' | 'battle' | 'results';

/** Bottom-sheet target: a mount, the planet itself, or nothing. */
export type SheetTarget = { kind: 'slot'; id: number } | { kind: 'planet' } | null;

/**
 * One-time hints. Each is dismissed by *doing the thing*, not by tapping an
 * acknowledge button, and never shown again — the game teaches by pointing at
 * the next verb and then getting out of the way.
 */
export type CoachId = 'build' | 'hold' | 'juice' | 'ultimate' | 'merge' | 'planet';

const COACH_KEY = 'playnets.coached.v1';

function loadCoached(): Record<CoachId, boolean> {
  const empty = {
    build: false,
    hold: false,
    juice: false,
    ultimate: false,
    merge: false,
    planet: false,
  };
  try {
    const raw = localStorage.getItem(COACH_KEY);
    return raw ? { ...empty, ...(JSON.parse(raw) as Record<CoachId, boolean>) } : empty;
  } catch {
    return empty;
  }
}

function mark(
  current: Record<CoachId, boolean>,
  id: CoachId,
): Record<CoachId, boolean> {
  if (current[id]) return current;
  const next = { ...current, [id]: true };
  try {
    localStorage.setItem(COACH_KEY, JSON.stringify(next));
  } catch {
    /* private browsing; the hints just show again next session */
  }
  return next;
}

export interface HudSnapshot {
  juice: number;
  coreHp: number;
  coreMax: number;
  wave: number;
  waveCount: number;
  waveState: World['waveState'];
  /** Seconds until the next wave, during prep. */
  prepLeft: number;
  ultCd: number;
  ultTotal: number;
  living: number;
  kills: number;
  /** Ring mass, so the HUD can show why the ring feels heavy. */
  ringLoad: number;
  /** True while the player is planning rather than fighting. */
  building: boolean;
  /** Value sitting uncollected on the field — the "go get it" nudge. */
  juiceOnField: number;
  juiceLost: number;
  /** Planet upgrade id → level bought. */
  upgrades: Record<string, number>;
  /** Whether the hold-to-start control should be offered at all. */
  canStart: boolean;
}

interface UiState {
  screen: Screen;
  planetId: string;
  levelId: string;
  /**
   * What the bottom sheet is editing. Tapping a mount opens its build/upgrade
   * options; tapping the planet itself opens the planet's own upgrades, which
   * is where they live now — there is no separate panel to go looking for.
   */
  sheet: SheetTarget;
  /**
   * The structure the player has picked up off the build bar and is carrying.
   * Placing is a two-part gesture — take a card, drop it on a mount — and this
   * is the middle of it. Null means empty-handed.
   */
  armed: string | null;
  /** Mount the carried card is currently over, for the drop highlight. */
  hoverSlot: number | null;
  /** Which one-time hints the player has already acted on. */
  coached: Record<CoachId, boolean>;
  paused: boolean;
  speed: 1 | 2;
  /** Placard line from the planet's voice, shown in the margin. */
  say: string;
  hud: HudSnapshot;

  setScreen: (s: Screen) => void;
  setPlanet: (id: string) => void;
  setLevel: (id: string) => void;
  openSheet: (target: SheetTarget) => void;
  arm: (structureId: string | null) => void;
  setHoverSlot: (id: number | null) => void;
  markCoached: (id: CoachId) => void;
  setPaused: (p: boolean) => void;
  toggleSpeed: () => void;
  setSay: (line: string) => void;
  pushHud: (h: HudSnapshot) => void;
}

const emptyHud: HudSnapshot = {
  juice: 0,
  coreHp: 100,
  coreMax: 100,
  wave: 1,
  waveCount: 1,
  waveState: 'prep',
  prepLeft: 0,
  ultCd: 0,
  ultTotal: 1,
  living: 0,
  kills: 0,
  ringLoad: 1,
  building: true,
  juiceOnField: 0,
  juiceLost: 0,
  upgrades: {},
  canStart: false,
};

export const useUi = create<UiState>((set) => ({
  screen: 'select',
  planetId: 'terra',
  // Derived, not a literal: a hardcoded id silently rotted through the
  // region rename and only surfaced once the game stopped going via the map.
  levelId: LEVELS[0]!.id,
  sheet: null,
  armed: null,
  hoverSlot: null,
  coached: loadCoached(),
  paused: false,
  speed: 1,
  say: '',
  hud: emptyHud,

  setScreen: (screen) => set({ screen }),
  setPlanet: (planetId) => set({ planetId }),
  setLevel: (levelId) => set({ levelId }),
  arm: (armed) => set({ armed, hoverSlot: null }),
  setHoverSlot: (hoverSlot) => set({ hoverSlot }),
  openSheet: (sheet) =>
    set((s) => ({
      sheet,
      // Opening a thing is proof enough that the player found it.
      coached:
        sheet?.kind === 'slot'
          ? mark(s.coached, 'build')
          : sheet?.kind === 'planet'
            ? mark(s.coached, 'planet')
            : s.coached,
    })),
  markCoached: (id) => set((s) => ({ coached: mark(s.coached, id) })),
  setPaused: (paused) => set({ paused }),
  toggleSpeed: () => set((s) => ({ speed: s.speed === 1 ? 2 : 1 })),
  setSay: (say) => set({ say }),
  pushHud: (hud) => set({ hud }),
}));

/** Build the flat snapshot. Called at 10Hz from the game loop, never per tick. */
export function snapshotHud(world: World, ringLoad: number): HudSnapshot {
  let living = 0;
  for (const e of world.enemies) if (e.alive) living++;
  let juiceOnField = 0;
  for (const d of world.drops) if (d.alive && !d.collected) juiceOnField += d.value;
  return {
    juice: Math.floor(world.juice),
    coreHp: Math.max(0, Math.ceil(world.coreHp)),
    coreMax: world.coreMax,
    wave: Math.min(world.waveIndex + 1, world.level.waves.length),
    waveCount: world.level.waves.length,
    waveState: world.waveState,
    prepLeft: Math.max(0, world.waveTimer),
    ultCd: world.ultCd,
    ultTotal: world.ultimate.cooldown,
    living,
    kills: world.stats.kills,
    ringLoad,
    building: world.waveState === 'prep',
    juiceOnField,
    juiceLost: world.stats.juiceLost,
    upgrades: { ...world.upgrades },
    canStart: canStartWave(world),
  };
}
